import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import {
  BarChart,
  CartesianGrid,
  Bar,
  XAxis,
  YAxis,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { DateTime, Duration } from "luxon";

const DashboardLineChart = ({
  data,
  title,
  alias,
  payload,
  options,
  cbtiTickets,
}) => {
  const [avgChartData, setAvgChartData] = useState([]);
  const [dailyChartData, setDailyChartData] = useState([]);
  // Transform the data into the format required by Recharts
  useEffect(() => {
    const sortedAvgData = Object.values(data)
      .map((d) => ({ x: d.x, y: [d.mu, d.mu] }))
      .sort((a, b) => a.x - b.x);
    const sortedDailyData = payload
      .map((entry, i) => {
        let value = entry?.data[alias] || "null";
        if (value.startsWith("PT")) {
          value = Duration.fromISO(value).as("minutes");
          value = value < 1 ? 1 : value;
        } else if (title === "Sleep Quality") {
          value = parseInt(value) + 1;
        } else {
          value = parseFloat(value);
        }
        return {
          dstr: entry.dateAvailable,
          x: DateTime.fromISO(entry.dateAvailable)
            .toLocal()
            .startOf("day")
            .toMillis(),
          y: value ? value : null,
        };
      })
      .filter((d) => d.x !== null && d.y !== null);
    // Undublicate the data
    const dedupedDailyData = sortedDailyData.reduceRight(
      (acc, item) => {
        if (!acc.seen.has(item.dstr)) {
          acc.seen.add(item.dstr);
          acc.result.unshift(item); // maintain original order
        }
        return acc;
      },
      { seen: new Set(), result: [] },
    ).result;
    setDailyChartData(dedupedDailyData);
    setAvgChartData(sortedAvgData);
  }, [data]); // eslint-disable-line
  return (
    <Box height={150}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={dailyChartData} barGap={0} barCategoryGap={0}>
          <XAxis
            allowDataOverflow={true}
            type="number"
            dataKey="x"
            scale="time"
            domain={([dataMin, dataMax]) => {
              const minCompleted = payload
                .filter((d) => d.dateCompleted)
                .map((d) =>
                  DateTime.fromISO(d.dateAvailable).toLocal().toMillis(),
                )
                .sort((a, b) => a - b)[0];
              const maxCompleted = payload
                .filter((d) => d.dateCompleted)
                .map((d) =>
                  DateTime.fromISO(d.dateAvailable).toLocal().toMillis(),
                )
                .sort((a, b) => b - a)[0];
              return [minCompleted - 86400000, maxCompleted + 86400000];
            }}
            tickSize={0}
            interval={0}
            tick={{ fontSize: 8, textAnchor: "end" }}
            ticks={
              cbtiTickets && cbtiTickets[0]
                ? cbtiTickets.map((entry) => {
                    return (
                      DateTime.fromISO(entry.dateAvailable)
                        .toLocal()
                        .startOf("day")
                        .toMillis() +
                      10 * 60 * 60 * 1000
                    );
                  })
                : payload.map((entry) => {
                    return DateTime.fromISO(entry.dateAvailable)
                      .toLocal()
                      .startOf("day")
                      .toMillis();
                  })
            }
            tickFormatter={(tick, i) => {
              return cbtiTickets && cbtiTickets[i]
                ? cbtiTickets[i].acronym
                : DateTime.fromMillis(tick).toLocal().toFormat("EEEEE dd/MM");
            }}
            angle={-90}
          />
          <YAxis
            allowDataOverflow={true}
            domain={options.domain}
            type="number"
            label={{
              value: title,
              angle: -90,
              fontSize: 12,
            }}
            ticks={options.ticks}
            tick={{ fontSize: 10 }}
            tickSize={0}
            interval={0}
          />
          <Bar dataKey="y" stackId="abc" fill="#81C4FF" />
          {avgChartData.map((avg, i) => (
            <ReferenceLine
              key={`ref-${title}-${i}-${avg.x}-${avg.y}`}
              stroke="#164BA7"
              strokeWidth={3}
              segment={[
                {
                  x: avg.x[0] + 24 * 60 * 60 * 250,
                  y: avg.y[0],
                },
                {
                  x: avg.x[1] + 24 * 60 * 60 * 250,
                  y: avg.y[1],
                },
              ]}
            />
          ))}
          <CartesianGrid
            stroke="#888"
            strokeDasharray="1 3"
            vertical={cbtiTickets && cbtiTickets[0] ? true : false}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default DashboardLineChart;
